/**
 * HTTP/API/queries for Timely resources.
 */

import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';

extendZodWithOpenApi(z);

import { timelyIntegrationCloudV1UserAccountsSchema } from '@timelyapp/timely-api-spec';
import {
  DateTimeTzSchema,
  GenericConnectionId,
  GenericTimelyAccountId,
  GenericUUID,
} from '../models.generic';
import {
  TimelyResourcesDataSchema,
  TimelyResourceTypeEnumsSchema,
} from './models.timely.resources';
import { QueryReturnedDataDiffSchema } from './models.timely.resources.db';

/**
 * Post-import callback from Timely
 * @todo Re-work this wrt other schemas, probably.
 * @note We'll need `identity_id` for imported users, somehow.
 */

export type ResourceErrorObject = z.infer<typeof ResourceErrorObjectSchema>;
const ResourceErrorObjectSchema = z.object({
  scope: z.string().describe('The scope of the error from Rails, usually a field name'),
  message: z.string().describe("The error message, e.g., 'can't be blank'"),
});

export type ProcessedTimelyDataDiffDataRelationships = z.infer<
  typeof ProcessedTimelyDataDiffDataRelationshipsSchema
>;
export const ProcessedTimelyDataDiffDataRelationshipsSchema = z.object({
  tic_id: GenericUUID.describe('The TIC `id` reference to the `resource` or `identity`'),
  timely_id: z
    .number()
    .int()
    .nullable()
    .describe('The Timely ID of the resource we inserted/updated'),
  timely_account_id: GenericTimelyAccountId.nullable(),
  action: z
    .enum(['insert', 'update', 'archive', 'unknown'])
    .describe('The action taken on Timelys side'),
  type: TimelyResourceTypeEnumsSchema.describe(
    'The resource type (as Timely sees it). Mostly useful to identify `tic_id` in cases where the data is not a `resource` (e.g, user)'
  ),
  errors: z
    .array(ResourceErrorObjectSchema)
    .describe(
      'Errors by field, can be an empty array or contain objects with `scope` and `message` properties'
    ),
  // ref DM with Kamil @ https://timely.slack.com/archives/D02LWV2G01H/p1733416167950339
  side_effects: timelyIntegrationCloudV1UserAccountsSchema
    .nullable()
    .describe(
      'If an identity created or linked with a timely user, the timely_user and their timely_accounts are returned.'
    ),
});

/**
 * Diff status states (internal/external)
 */
const timelyDataDiffExternalStatusEnums = ['success', 'failed'] as const;
const timelyDataDiffInternalStatusEnums = ['created', 'in_progress'] as const;

const TimelyDataDiffStatusEnumsSchema = z
  .enum([...timelyDataDiffInternalStatusEnums, ...timelyDataDiffExternalStatusEnums])
  .describe('All possible states of a Timely data diff, both internal (TIC) and external (Timely)');

export type ProcessedTimelyDataDiffCallback = z.infer<typeof ProcessedTimelyDataDiffCallbackSchema>;
export const ProcessedTimelyDataDiffCallbackSchema = z.object({
  diff_id: z.string().uuid().describe('Data diff UUID identifying the diff'),
  timely_account_id: GenericTimelyAccountId,
  status: z
    .enum(timelyDataDiffExternalStatusEnums)
    .describe('The status of the Timely job outcome'),
  error: z
    .string()
    .nullable()
    .default(null)
    .openapi({ description: 'An error message if the job failed', example: null }),
  failed_count: z.number().int().min(0).describe('An integer count of resource sync failures'),
  data: z.array(ProcessedTimelyDataDiffDataRelationshipsSchema),
});

/**
 * Diff ID Query/Params
 */
export type GetTimelyDataDiffIdQuery = z.infer<typeof GetTimelyDataDiffIdQuerySchema>;
export const GetTimelyDataDiffIdQuerySchema = z.object({
  diffId: GenericUUID,
});

export type GetTimelyDataDiffScopingQuery = z.infer<typeof GetTimelyDataDiffScopingQuerySchema>;
export const GetTimelyDataDiffScopingQuerySchema = z.object({
  timelyAccountId: GenericTimelyAccountId.optional().describe(
    'The Timely account ID to scope the diff to, if provided, will only return data for this account'
  ),
  account_id: GenericTimelyAccountId.optional().describe(
    'TEMPORARY: Rails legacy parameter name for timelyAccountId. Use timelyAccountId in new code.'
  ),
  connectionId: GenericConnectionId.optional().describe(
    'The connection ID to scope the diff to, if provided, will only return data for this connection'
  ),
  includeAllData: z
    .boolean()
    .optional()
    .default(false)
    .describe('Whether to include all data or only incremental changes'),
});

/**
 * List of recent data diffs (minimal data)
 */
export type ReadTimelyDataDiffIndex = z.infer<typeof ReadTimelyDataDiffIndexSchema>;
export const ReadTimelyDataDiffIndexSchema = z.object({
  id: GenericUUID,
  generated_at: DateTimeTzSchema,
  timely_account_id: GenericTimelyAccountId.nullable().openapi({
    description: 'The Timely account ID this diff belongs to',
  }),
  processed_by_timely_at: DateTimeTzSchema.nullable().describe(
    'Signals if & when Timely processed the diff'
  ),
  status: TimelyDataDiffStatusEnumsSchema,
  error: ProcessedTimelyDataDiffCallbackSchema.shape.error,
  failed_count: ProcessedTimelyDataDiffCallbackSchema.shape.failed_count,
  // Mostly for debugging/FYI about placeholders.
  message: z
    .string()
    .optional()
    .openapi({ description: 'A message about the diff', example: 'Some debug message?' }),
  link: z.string().url().nullable().default(null).describe('The full URL to the diff dump'),
});

/**
 * Timely's preferred envelope/structure for importing resources.
 */
export type ReadTimelyDataDiffRow = z.infer<typeof ReadTimelyDataDiffRowSchema>;
export const ReadTimelyDataDiffRowSchema = ReadTimelyDataDiffIndexSchema.extend({
  data: QueryReturnedDataDiffSchema,
  processed_output: ProcessedTimelyDataDiffCallbackSchema.nullable(),
});

/**
 * Presentable Diff is a more minimal, Timely-compatible format
 */
export type PresentableTimelyDataDiff = z.infer<typeof PresentableTimelyDataDiffSchema>;
export const PresentableTimelyDataDiffSchema = ReadTimelyDataDiffIndexSchema.extend({
  data: TimelyResourcesDataSchema,
});

export type TimelyDataDiffWebhookPayload = z.infer<typeof TimelyDataDiffWebhookPayloadSchema>;
export const TimelyDataDiffWebhookPayloadSchema = z.object({
  id: GenericUUID.describe('Primary diff identifier'),
  timely_account_id: GenericTimelyAccountId,
  link: z.string().url().describe('Direct link to retrieve the diff payload'),
  additional_diff_ids: z
    .array(
      z.object({
        id: GenericUUID.describe('Diff identifier for additional chunks in this run'),
        link: z.string().url(),
      })
    )
    .nonempty()
    .optional()
    .describe('Any subsequent diff IDs Timely should fetch for this run'),
  run_id: z.string().optional().describe('Correlation identifier for the generation run'),
});
